import { CompositeChart } from '@mantine/charts'
import type { ComboboxData } from '@mantine/core'
import { ActionIcon, Group, Paper, Select, Stack, Tabs, Text, Title } from '@mantine/core'
import {
  IconChevronLeft,
  IconChevronRight,
  IconEyeExclamation,
  IconMessage,
  IconPencil,
  IconRuler2,
  IconRulerMeasure,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { ResponsiveContainer } from 'recharts'

import { StatInfoGraphic } from '@/components/StatInfoGraphic'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetContentModuleResponse } from '@/services/api/api.types'
import { selectStyles } from '@/styles/Chart.css'
import { conversationsSeries, formatChartDate, getExplicitDateRange, isBeforeCreationDate } from '@/utils/analytics'
import { isHelpModule } from '@/utils/contentModuleTables'

import { ConversationByDateTooltip } from './ConversationByDateTooltip'
import { NoDataAvailable } from './NoDataAvailable'
import TabChartWrapper from './TabChartWrapper'

const TabKeys = {
  conversationLineChart: 'conversationLineChart',
  messageLineChart: 'messageLineChart',
} as const

export default function ContentModuleInsights() {
  const { api, authenticationStore } = useGlobalState()
  const { user, isSponsorAdmin } = authenticationStore

  const [selectedCm, setSelectedCm] = useState<GetContentModuleResponse | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>(TabKeys.conversationLineChart)
  const [backPeriod, setBackPeriod] = useState(0)

  const { data: sponsorInfo } = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    staleTime: 5 * 60 * 1000,
  })

  const cmQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    initialData: [],
    refetchInterval: 7 * 1000,
  })

  const dateRange = useMemo(() => getExplicitDateRange(backPeriod), [backPeriod])

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics', 'cmConversationsByDate', selectedCm?.taskId, backPeriod],
    queryFn: () =>
      api.getCmConversationsByDate({
        cm_id: selectedCm?.taskId ?? '',
        ...dateRange.analyticsParams,
      }),
    enabled: !!selectedCm?.taskId,
  })

  const handlePreviousPeriod = () => setBackPeriod(prev => prev - 1)
  const handleNextPeriod = () => setBackPeriod(prev => prev + 1)

  const nextPeriodDateRange = useMemo(() => getExplicitDateRange(backPeriod - 1), [backPeriod])
  const canGoPrevious = !isBeforeCreationDate(nextPeriodDateRange.startDate, sponsorInfo?.createdAt)
  const canGoNext = backPeriod < 0

  // Only show published content modules in the dropdown that are NOT Help Modules
  const publishedCms = cmQuery.data.filter(cm => cm.status === 'published' && !isHelpModule(cm))

  // Experts can only choose content modules they own; admins/PMs see all
  const selectableCms = isSponsorAdmin ? publishedCms : publishedCms.filter(cm => cm.createdBy === user?.userId)

  const cmOptions: ComboboxData = selectableCms.map(({ taskId, name }) => ({
    value: taskId ?? '',
    label: name ?? '',
    disabled: !taskId,
  }))

  useEffect(() => {
    const currentInSelectable = selectedCm?.taskId != null && selectableCms.some(cm => cm.taskId === selectedCm.taskId)
    if (!currentInSelectable && selectedCm) {
      setSelectedCm(null)
    }
    if (selectableCms.length > 0 && !selectedCm) {
      setSelectedCm(selectableCms[0])
    }
  }, [selectableCms, selectedCm])

  const hasConversationData = data?.data.length ?? 0 > 0

  return (
    <Stack>
      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Group justify="space-between" mb="md" align="top">
          <Select
            data={cmOptions}
            label="Content Module"
            placeholder="Select a content module"
            value={selectedCm?.taskId ?? null}
            onChange={value => {
              const cm = cmQuery.data.find(cm => cm.taskId === value)
              setSelectedCm(cm ?? null)
            }}
            miw={300}
            classNames={selectStyles}
          />
          <Stack align="flex-end" gap="md">
            <Group gap="xs" align="center">
              <ActionIcon
                variant="subtle"
                onClick={handlePreviousPeriod}
                disabled={!canGoPrevious}
                aria-label="Previous period">
                <IconChevronLeft size={20} />
              </ActionIcon>

              <Text size="sm" c="dimmed" style={{ minWidth: 180, textAlign: 'center' }}>
                {formatChartDate(dateRange.startDate, true)} — {formatChartDate(dateRange.endDate, true)}
              </Text>

              <ActionIcon variant="subtle" onClick={handleNextPeriod} disabled={!canGoNext} aria-label="Next period">
                <IconChevronRight size={20} />
              </ActionIcon>
            </Group>

            <Group gap="xl">
              <StatInfoGraphic
                icon={IconEyeExclamation}
                label="Unique Visitors"
                value={data?.stats.totalUniqueConversations ?? 0}
                tooltipText="Number of unique members who interacted with this content module"
              />

              <StatInfoGraphic
                icon={IconMessage}
                label="Conversations"
                value={data?.stats.totalConversations ?? 0}
                tooltipText="Total number of conversations for this content module"
              />

              <StatInfoGraphic
                icon={IconPencil}
                label="Member Messages"
                value={data?.stats.totalMessages ?? 0}
                tooltipText="Total number of messages sent by members using this content module"
              />

              <StatInfoGraphic
                icon={IconRuler2}
                label="Average Length"
                value={Math.round(data?.stats.averageConversationLength ?? 0)}
                tooltipText="Average length of a conversation using this content module"
              />

              <StatInfoGraphic
                icon={IconRulerMeasure}
                label="Max Length"
                value={data?.stats.maxConversationLength ?? 0}
                tooltipText="Longest conversation using this content module"
              />
            </Group>
          </Stack>
        </Group>
      </Paper>

      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value={TabKeys.conversationLineChart}>Conversations</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value={TabKeys.conversationLineChart}>
            <Group justify="space-between" align="baseline">
              <Title order={6} mb="md" pt="md">
                Number of Conversations over Time
              </Title>
            </Group>

            <TabChartWrapper isActive={activeTab === TabKeys.conversationLineChart} isLoading={isLoading} error={error}>
              <ResponsiveContainer width="100%" height={300}>
                {hasConversationData ? (
                  <CompositeChart
                    data={data?.data ?? []}
                    dataKey="date"
                    curveType="linear"
                    series={conversationsSeries}
                    xAxisProps={{
                      tickFormatter: (date: string) => {
                        return formatChartDate(date)
                      },
                      angle: -45,
                      textAnchor: 'end',
                      interval: 0,
                    }}
                    tooltipProps={{
                      content: ({ payload, label }) => {
                        if (!payload || payload.length === 0) return null

                        // Find the total value for this date point
                        const totalValue = payload.find(entry => entry.name === 'total')?.value || 0
                        const allConversationsValue =
                          payload.find(entry => entry.name === 'allConversations')?.value || 1
                        const percentage = (totalValue / allConversationsValue) * 100

                        return <ConversationByDateTooltip label={label} percentage={percentage} payload={payload} />
                      },
                    }}
                  />
                ) : (
                  <NoDataAvailable />
                )}
              </ResponsiveContainer>
            </TabChartWrapper>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  )
}
